/**
 * [DYSKI 2026-09-12] HTTP obszaru dysków: import z pliku, automapowanie właścicieli, sekcja „zasoby bez
 * właściciela” i podgląd nazwy dla formularza zakładania dysku.
 *
 * Wzorzec `pages/admin/categoriesApi.js`: ta sama instancja axiosa co cała aplikacja (`apiService.api`),
 * więc przechwytywacz JWT i obsługa 401 działają bez zmian — a `api/apiService.js` zostaje NIETKNIĘTY
 * (w tej fali pracują w nim równolegle inne obszary).
 */
import apiService from "../../api/apiService";

/**
 * Klient rozwiązywany PRZY WYWOŁANIU, nie przy imporcie.
 *
 * ⚠ `const http = apiService.api` na poziomie modułu zamraża wartość z chwili importu. W testach
 * (i wszędzie tam, gdzie `apiService` jest podmieniany) moduł bywa wczytany, ZANIM podmiana doda
 * pole `api` — wtedy `http` zostaje na zawsze `undefined`, a pierwsze żądanie wywraca się na
 * „Cannot read properties of undefined", zamiast po prostu pójść na serwer. Odczyt leniwy kosztuje
 * jedno wyłuskanie pola i usuwa całą tę klasę błędów.
 */
const http = () => {
    const klient = apiService?.api;
    if (!klient) {
        throw new Error("Moduł dysków: apiService.api nie jest dostępny — nie ma czym wysłać żądania.");
    }
    return klient;
};

/**
 * 🔴 PODGLĄD I ZAPIS TO DWA RÓŻNE ADRESY, nie jeden z flagą. Adres, który raz czyta, a raz pisze,
 * prędzej czy później zostanie zawołany z domyślną wartością parametru — i kilkaset zasobów wjedzie
 * do bazy przy próbie zobaczenia, co by się stało.
 */
const dyskiApi = {
    // ── IMPORT Z PLIKU ──────────────────────────────────────────────────────────────────────
    /** Podgląd importu: ile zasobów powstanie, kogo rozpoznano, czego NIE rozpoznano. Nic nie zapisuje. */
    podgladImportu: (plik) => {
        const dane = new FormData();
        dane.append("plik", plik);
        return http().post("/dyski-import/podglad", dane);
    },

    /** Zapis importu. Dodaje brakujące zasoby; istniejących NIE rusza (najwyżej uzupełnia brak właściciela). */
    zapiszImport: (plik) => {
        const dane = new FormData();
        dane.append("plik", plik);
        return http().post("/dyski-import/zapisz", dane);
    },

    // ── AUTOMAPOWANIE ───────────────────────────────────────────────────────────────────────
    /** Podgląd automapowania: ile zostanie zmapowanych, ile POMINIĘTYCH (bo mają właściciela) i dlaczego. */
    podgladAutomapowania: () => http().post("/dyski-import/automapowanie/podglad"),

    /** Zapis automapowania. Rusza WYŁĄCZNIE zasoby bez właściciela-konta. */
    zapiszAutomapowanie: () => http().post("/dyski-import/automapowanie/zapisz"),

    // ── SEKCJA „ZASOBY BEZ WŁAŚCICIELA” ─────────────────────────────────────────────────────
    bezWlasciciela: () => http().get("/dyski-import/bez-wlasciciela"),

    // ── TRWAŁY ŚLAD „NIEDOPASOWANE” (L2, 2026-09-19) ────────────────────────────────────────
    /**
     * Zapisy z listy dysków, których nie dało się powiązać z kontem — lista, do której DA SIĘ WRÓCIĆ.
     *
     * 🔴 Do 2026-09-19 ta lista istniała wyłącznie w odpowiedzi na zapis importu: administrator zamykał
     * okno i 43 pozycje do ręcznego rozstrzygnięcia znikały bezpowrotnie. Osobny adres, bo czytanie tej
     * listy nie ma nic wspólnego z wgrywaniem pliku.
     */
    niedopasowani: (takzeRozstrzygniete = false) =>
        http().get("/dyski-import/niedopasowani", { params: { takzeRozstrzygniete } }),

    /** Zamknięcie (albo ponowne otwarcie) jednej pozycji listy niedopasowanych. */
    rozstrzygnijNiedopasowanie: (id, rozstrzygniete, notatka) =>
        http().post(`/dyski-import/niedopasowani/${id}/rozstrzygnij`, {
            rozstrzygniete: Boolean(rozstrzygniete),
            notatka: notatka || null,
        }),

    // ── WŁAŚCICIEL WITRYNY Z MICROSOFT 365 (L4, 2026-09-19) ─────────────────────────────────
    /**
     * Kogo Microsoft 365 podaje jako właściciela TEJ witryny — z odpowiedzią także wtedy, gdy się NIE DA.
     *
     * ⚠ ODPOWIEDŹ MA DWA KSZTAŁTY I OBA SĄ POPRAWNE: `{ustalono: true, zrodlo, wlasciciele: [...]}`
     * albo `{ustalono: false, powod: '…', brakujacyZakres: '…'}`. Pusta lista przy `ustalono: false`
     * NIE znaczy „witryna nie ma właścicieli” — znaczy „SharePoint tego nie wystawia”. Ekran musi
     * pokazać `powod`, bo inaczej człowiek odczyta brak danych jako brak opiekuna.
     */
    wlascicielSharePoint: (id) => http().get(`/dyski-import/${id}/wlasciciel-sharepoint`),

    /** Wstawia właściciela z Microsoft 365 na zasób i ZAPISUJE, że pochodzi z SharePointa. */
    zapiszWlascicielaSharePoint: (id) => http().post(`/dyski-import/${id}/wlasciciel-sharepoint`),

    // ── FORMULARZ NOWEGO DYSKU ──────────────────────────────────────────────────────────────
    /**
     * Składanie nazwy technicznej i ścieżki z przedrostka + sprawdzenie, czy nazwa nie jest ZAJĘTA.
     * Tylko to drugie wymaga serwera; samo składanie robi `regulaNazwyDysku.js` na żywo, przy każdym
     * wpisanym znaku, bez żądania HTTP.
     */
    podgladNazwy: (rodzaj, nazwa) =>
        http().get("/dyski-import/podglad-nazwy", { params: { rodzaj, nazwa } }),

    // ── WITRYNY SHAREPOINT ──────────────────────────────────────────────────────────────────
    /**
     * Witryny SharePoint organizacji do wyboru przy zakładaniu zasobu.
     *
     * Cytat właściciela: „dodajesz tutaj też sharepoint w zakresie tych dodanych np. tutaj lista
     * /moje-pliki > zakładka Witryny Sharepoint".
     *
     * ⚠ ODPOWIEDŹ MA DWA KSZTAŁTY I OBA SĄ POPRAWNE: `{zrodlo: 'graph-delegowany', items: [...]}`
     * albo `{zrodlo: 'niedostepne', items: [], komunikat: '…'}` — to drugie, gdy konto nie jest
     * połączone z Microsoft 365 albo brak zgody `Sites.Read.All`. Brak obsługi drugiego przypadku
     * daje PUSTĄ listę bez słowa wyjaśnienia, a administrator wnioskuje z niej, że organizacja
     * nie ma witryn. Wołający MUSI rozróżnić „nie ma witryn” od „nie udało się zapytać”.
     */
    witrynySharePoint: (search) =>
        http().get("/dynamic-settings/drives/sharepoint-sites", { params: { search } }),

    // ── SYNCHRONIZACJA WITRYN SHAREPOINT DO EWIDENCJI (R17, 2026-09-13) ──────────────────────
    /**
     * Podgląd: co zostanie dodane, co już jest, co dostanie znacznik „brakuje". Nic nie zapisuje.
     * POST (nie GET), bo pobranie z Graph trwa i nie może trafić do sklejania/cache GET-ów.
     */
    podgladSynchronizacjiSharePoint: () => http().post("/dyski-import/sharepoint/podglad"),

    /** Zapis synchronizacji. Dodaje brakujące, uzupełnia istniejące, ZNACZY nieobecne — NICZEGO nie kasuje. */
    zapiszSynchronizacjeSharePoint: () => http().post("/dyski-import/sharepoint/zapisz"),

    // ── NAZWA KANONICZNA I OPIS (R18, 2026-09-13) ───────────────────────────────────────────
    /** Masowe uzupełnienie — domyślnie TYLKO puste pola; `nadpisz` = jawna decyzja o wygenerowaniu na nowo. */
    uzupelnijNazwy: (ids, nadpisz = false) =>
        http().post("/dyski-import/nazwy/uzupelnij", { ids: ids || [], nadpisz: Boolean(nadpisz) }),

    /** Edycja w wierszu: dwa pola jednego zasobu (nie rusza właściciela, działu ani nazwy technicznej). */
    zapiszNazwy: (id, dane) =>
        http().put(`/dyski-import/nazwy/${id}`, {
            nazwaKanoniczna: dane?.nazwaKanoniczna ?? "",
            opis: dane?.opis ?? "",
        }),
};

export default dyskiApi;
